import { Link } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import { OtherOrgBanner } from "@/shared/components";
import { ingestionRuleIdOf } from "@/shared/domain/ingestionRules";
import { isManaged, type TriggerRecord } from "@/shared/domain/triggers";
import { useTrigger, useUpdateTrigger } from "@/shared/domain/triggers/hooks";
import { Banner, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { ManagedByIngestionRuleNote } from "../Trigger/ManagedByIngestionRuleNote";
import { systemManagedExplanation } from "../Trigger/managedTriggerExplanation";
import { isEditableInForm } from "../TriggerForm/formScope";
import { TriggerForm } from "../TriggerForm/TriggerForm";
import { TriggerFormPage } from "../TriggerForm/TriggerFormPage";
import { useTriggerRouteContext } from "../useTriggerRouteContext";

import styles from "./EditTrigger.module.css";

/**
 * Edit one stored trigger on the same page it was created on.
 *
 * Two kinds of trigger are turned away with the reason. A managed trigger's
 * definition belongs to Roboto or to an ingestion rule, and the API refuses
 * to save it. A trigger the form has no controls for would be opened
 * half-represented, and saving it would quietly rewrite the parts that were
 * never shown.
 */
export function EditTrigger() {
  const { currentOrganization } = useAuth();
  const { triggerId } = useTriggerRouteContext();
  const triggerQuery = useTrigger(triggerId);
  const record = triggerQuery.data;

  return (
    <TriggerFormPage
      title="Edit trigger"
      subtitle={
        "Change what this trigger watches, how often it fires, and what it runs."
      }
      crumbs={
        record !== undefined && (
          <Link to={Goto.createTriggerRoute(record.trigger_id, record.name)}>
            {record.name}
          </Link>
        )
      }
    >
      {triggerQuery.isLoading ? <Spinner /> : null}
      {triggerQuery.error === null ? null : (
        <Banner
          variant="error"
          className={styles.notice}
          data-testid="edit-trigger-error"
        >
          {triggerQuery.error.message}
        </Banner>
      )}
      {record !== undefined && (
        <>
          <OtherOrgBanner orgId={record.org_id}>
            {({ orgName }) => `Switch to ${orgName} to edit this trigger.`}
          </OtherOrgBanner>
          {/* Only in the trigger's own org: the form's agent picker, Slack
              channels and RoboQL field read the current org, so saving from
              another org would write its ids into this trigger. */}
          {record.org_id === currentOrganization?.org_id && (
            <Editor record={record} />
          )}
        </>
      )}
    </TriggerFormPage>
  );
}

function Editor({ record }: { record: TriggerRecord }) {
  const { mutateAsync: updateTrigger } = useUpdateTrigger();
  const { goto } = useNavigation();
  const backToTrigger = () => goto.trigger(record.trigger_id, record.name);

  if (isManaged(record) || !isEditableInForm(record)) {
    return (
      <Banner
        variant="warning"
        className={styles.notice}
        data-testid="edit-trigger-unavailable"
        description={<WhyNotEditable record={record} />}
        action={
          <Link to={Goto.createTriggerRoute(record.trigger_id, record.name)}>
            Back to {record.name}
          </Link>
        }
      />
    );
  }

  return (
    <TriggerForm
      mode="edit"
      initial={record}
      onSubmit={async (request) => {
        // The form's request always carries a name. A name in an update
        // renames the trigger, so it is sent only when it differs from the
        // stored one.
        const { name, ...updates } = request;
        const saved = await updateTrigger({
          triggerId: record.trigger_id,
          updates: name === record.name ? updates : { ...updates, name },
        });
        goto.trigger(saved.trigger_id, saved.name);
      }}
      onCancel={backToTrigger}
    />
  );
}

function WhyNotEditable({ record }: { record: TriggerRecord }) {
  const ruleId = ingestionRuleIdOf(record);
  if (ruleId !== null) {
    return <ManagedByIngestionRuleNote ruleId={ruleId} />;
  }
  if (isManaged(record)) {
    return <>{systemManagedExplanation(record.managed_by)}</>;
  }
  return (
    <>
      This trigger watches something the form cannot show, so editing it here
      would drop what is not on the page. It can still be enabled, disabled, and
      deleted from its own page.
    </>
  );
}
